import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { GKLoading, GKLoadingType } from "@granular/gds";
import { LoginCardLayout } from "../../LoginCardLayout";
import { getAuthEnv, makeAuthApp } from "../../../services/auth/new-msal/init";
import { useMount } from "../../..";
import { B_TO_C_POLICIES, getAuthConfig } from "@granular/fabric3-definitions";

/**
 * Handles invite redemption by initiating the Azure B2C signup flow with PKCE.
 *
 * This component is the entry point from invite emails. It:
 * 1. Receives invite token from URL parameter
 * 2. Stores the invite context
 * 3. Initiates Azure B2C signup/signin flow (MSAL handles PKCE automatically)
 * 4. After user completes signup, Azure B2C redirects back to /login/auto-login-invite
 *
 * Flow:
 * Email → /invite/redeem?token=xyz → Initiate B2C with PKCE → User signs up →
 * B2C redirects → /login/auto-login-invite → Auto-login
 */
export const InviteRedeem: FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();

  useMount(() => {
    void (async () => {
      try {
        // Get invite token from URL
        const searchParams = new URLSearchParams(globalThis.location.search);
        const inviteToken = searchParams.get("token");

        if (!inviteToken) {
          console.error("[Invite] No invite token provided");
          setError(
            "Invalid invite link. Please check your email and try again.",
          );
          return;
        }

        console.log(
          "[Invite] Received invite token, initiating B2C signup flow",
        );
        console.log("[Invite] Token length:", inviteToken.length);

        // Store invite token for later (after signup completes)
        sessionStorage.setItem("invite-token", inviteToken);
        sessionStorage.setItem("invite-flow-active", "true");

        // Always use B2C for invite signups (growers)
        // Override authority to use the invite signup policy (B2C_1A_Granular_Signup_redeem)
        // instead of the general signin policy (B2C_1A_GRANULAR_SIGNIN)
        const authConfig = getAuthConfig(
          getAuthEnv(),
          "b2c",
          B_TO_C_POLICIES.REDEEM,
        );

        const authApp = await makeAuthApp(authConfig);

        // Build the redirect URI - after signup, user returns to auto-login-invite
        const redirectUri = `${globalThis.location.origin}/login/auto-login-invite`;

        // Build scopes
        const appScope = `${authConfig.appId}/internal`;
        const scopes = [appScope, "offline_access", "openid", "profile"];

        console.log("[Invite] Redirecting to Azure B2C signup with PKCE...");
        console.log("[Invite] Authority:", authConfig.authority);
        console.log("[Invite] Redirect URI:", redirectUri);

        // Initiate the login redirect
        // MSAL will automatically:
        // 1. Generate PKCE code_verifier and code_challenge
        // 2. Store code_verifier in session storage
        // 3. Send code_challenge to Azure B2C
        // 4. After redirect back, use code_verifier to exchange the code
        await authApp.loginRedirect({
          scopes,
          redirectUri,
          // Pass the invite token to Azure B2C as an extra query parameter
          // Azure B2C custom policies typically use 'id_token_hint' for JWT tokens
          // Note: MSAL will automatically URL-encode this value
          extraQueryParameters: {
            id_token_hint: inviteToken,
          },
        });

        // Redirect happens, component will unmount
      } catch (error) {
        console.error("[Invite] Failed to initiate signup flow:", error);

        // Handle "interaction_in_progress" error specifically
        if (
          error instanceof Error &&
          error.message.includes("interaction_in_progress")
        ) {
          console.log(
            "[Invite] Interaction already in progress, waiting for redirect...",
          );
          return; // Don't show error, just wait
        }

        setError(
          `Failed to process invite: ${error instanceof Error ? error.message : "Unknown error"}. Please try again or contact support.`,
        );
      }
    })();
  });

  if (error) {
    return (
      <LoginCardLayout>
        <div className="text-center">
          <p className="text-danger mb-4">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/login")}
          >
            <FormattedMessage id="go_to_login" />
          </button>
        </div>
      </LoginCardLayout>
    );
  }

  return (
    <LoginCardLayout>
      <GKLoading
        className="pr-1"
        isLoading={true}
        type={GKLoadingType.Spinner}
        spinnerProps={{ width: 32 }}
      />
      <p className="mt-4">
        <FormattedMessage id="processing_your_invite" />
        ...
      </p>
    </LoginCardLayout>
  );
};
